import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import toast from 'react-hot-toast';
import {
    IconChevronLeft, IconSend, IconLoader,
    IconCheck, IconClock, IconMessageCircle, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import { use360RequestSummary, useActivate360Request } from '../../hooks/usePerformance';

function formatDate(d) {
    if (!d) return '–';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_DOT   = { draft: '#FC985E', active: 'var(--success)', closed: 'var(--success)' };
const STATUS_LABEL = { draft: 'Draft',   active: 'Active',         closed: 'Closed' };
const STATUS_CLASS = {
    draft:  'bg-warning/10 text-warning border-warning/20',
    active: 'bg-success/10 text-success border-success/20',
    closed: 'bg-success/10 text-success border-success/20',
};

const RATER_COLORS = {
    self:       { bg: 'bg-primary/10',  text: 'text-primary',  label: 'Self'          },
    supervisor: { bg: 'bg-info/10',     text: 'text-info',     label: 'Supervisor'    },
    supervisee: { bg: 'bg-success/10',  text: 'text-success',  label: 'Direct Report' },
    colleague:  { bg: 'bg-warning/10',  text: 'text-warning',  label: 'Colleague'     },
    external:   { bg: 'bg-purple/10',   text: 'text-purple',   label: 'External'      },
};

const AVATAR_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#3b82f6', '#f43f5e'];
function avatarColor(str = '') {
    return AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}
function initials(name = '') {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function AccordionSection({ label, responded, categories }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-20 py-8 bg-bg-light/40 hover:bg-bg-light/70 transition-colors"
            >
                <div className="flex items-center gap-10">
                    <span className="text-[12px] font-semibold text-text-primary">{label}</span>
                    <span className="text-[10px] font-bold px-6 py-1 rounded-full bg-primary/10 text-primary">
                        {responded} response{responded !== 1 ? 's' : ''}
                    </span>
                </div>
                {open ? <IconChevronUp size={13} className="text-text-secondary" /> : <IconChevronDown size={13} className="text-text-secondary" />}
            </button>
            {open && (
                <div className="px-20 pb-15 grid grid-cols-1 md:grid-cols-2 gap-10">
                    {categories.map((cat, ci) => (
                        <div key={ci} className="rounded-lg border border-border overflow-hidden">
                            <div className="px-12 py-7 bg-bg-light/50 border-b border-border">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">
                                    {cat.category_display || cat.category}
                                </span>
                            </div>
                            <div className="px-12 py-10 flex flex-col gap-8">
                                {cat.comments.map((c, j) => (
                                    <div key={j}>
                                        <p className="text-[10px] font-bold text-text-secondary mb-2">{c.rater_name}</p>
                                        <p className="text-[13px] text-text-primary leading-relaxed">{c.comment}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    return (
        <span className={`inline-flex items-center gap-5 px-8 py-3 rounded-full text-[11px] font-semibold border whitespace-nowrap ${STATUS_CLASS[status] || STATUS_CLASS.draft}`}>
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_DOT[status] || STATUS_DOT.draft }} />
            {STATUS_LABEL[status] || status}
        </span>
    );
}

export default function Feedback360Detail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const activateRequest = useActivate360Request();

    const { data: summary, isLoading } = use360RequestSummary(id);

    if (isLoading) return <PageLoader text="Loading summary..." />;
    if (!summary)  return null;

    const progressPct = summary.total_nominations > 0
        ? Math.round((summary.responded_count / summary.total_nominations) * 100)
        : 0;

    const allNominees = (summary.by_rater_type || []).flatMap(section =>
        (section.nominations || []).map(nom => ({ ...nom, rater_type: section.rater_type }))
    );

    const hasResponses = (summary.by_rater_type || []).some(s =>
        (s.categories || []).some(c => c.comments?.length > 0)
    );

    const handleActivate = async () => {
        try {
            await activateRequest.mutateAsync({ id });
            toast.success('Request activated! Emails sent to all nominees.');
        } catch {}
    };

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="360 Summary" path="Performance" />

            <button onClick={() => navigate('/performance/360-feedback')} className="btn btn-outline-secondary self-start">
                <IconChevronLeft size={15} /> Back
            </button>

            {/* Header card */}
            <div className="bg-bg-dark rounded-lg shadow-light border border-border p-20">
                <div className="flex flex-wrap items-start justify-between gap-15 mb-15">
                    <div>
                        <h2 className="text-[18px] font-bold text-text-primary">{summary.cycle_name}</h2>
                        <p className="text-sm text-text-secondary mt-3">
                            {summary.subject_name}
                            {summary.deadline ? <span className="ml-10">· Due {formatDate(summary.deadline)}</span> : ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-10">
                        <StatusPill status={summary.status} />
                        {summary.status === 'draft' && (
                            <button onClick={handleActivate} disabled={activateRequest.isPending} className="btn btn-primary">
                                {activateRequest.isPending ? <IconLoader className="animate-spin" size={14} /> : <IconSend size={14} />}
                                Send Requests
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-20 mb-12">
                    <span className="text-[11px] text-text-secondary">
                        <span className="font-bold text-text-primary text-[13px]">{summary.total_nominations ?? 0}</span> nominees
                    </span>
                    <span className="text-[11px] text-text-secondary">
                        <span className="font-bold text-success text-[13px]">{summary.responded_count ?? 0}</span> responded
                    </span>
                    <span className="text-[11px] text-text-secondary">
                        <span className="font-bold text-primary text-[13px]">{progressPct}%</span> response rate
                    </span>
                </div>

                <div className="h-[4px] bg-bg-light rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
            </div>

            {/* Nominees list */}
            <div className="bg-bg-dark rounded-lg shadow-light border border-border overflow-hidden">
                <div className="px-20 py-14 border-b border-border bg-bg-light/30">
                    <h3 className="text-[13px] font-semibold text-text-primary">Nominees</h3>
                </div>
                {allNominees.length === 0 ? (
                    <div className="p-20 text-center text-text-secondary text-[11px] italic">No nominees added yet.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {allNominees.map((nom, i) => {
                            const color     = RATER_COLORS[nom.rater_type] || RATER_COLORS.colleague;
                            const name      = nom.rater_name || nom.external_name || 'Rater';
                            const submitted = nom.status === 'submitted';
                            return (
                                <div key={i} className="flex items-center gap-10 px-15 py-6">
                                    <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: avatarColor(name) }}>
                                        {initials(name)}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <p className="text-[12px] font-medium text-text-primary truncate">{name}</p>
                                    </div>
                                    <div className="flex items-center gap-8 flex-shrink-0">
                                        <span className={`text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full ${color.bg} ${color.text}`}>
                                            {color.label}
                                        </span>
                                        <span className={`inline-flex items-center gap-3 text-[10px] font-medium ${submitted ? 'text-success' : 'text-text-secondary'}`}>
                                            {submitted ? <IconCheck size={11} /> : <IconClock size={11} />}
                                            {submitted ? 'Responded' : 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Responses — collapsible accordion per rater type */}
            {hasResponses && (
                <div className="bg-bg-dark rounded-lg shadow-light border border-border overflow-hidden">
                    <div className="px-20 py-14 border-b border-border bg-bg-light/30">
                        <h3 className="text-[13px] font-semibold text-text-primary">Feedback Received</h3>
                    </div>
                    <div className="divide-y divide-border">
                        {(summary.by_rater_type || []).map((section, si) => {
                            const responded = (section.nominations || []).filter(n => n.status === 'submitted').length;
                            if (responded === 0) return null;
                            const filledCats = (section.categories || []).filter(c => c.comments?.length > 0);
                            return (
                                <AccordionSection
                                    key={si}
                                    label={RATER_COLORS[section.rater_type]?.label || section.rater_type_display || section.rater_type}
                                    responded={responded}
                                    categories={filledCats}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* No responses yet */}
            {!hasResponses && summary.status !== 'draft' && (
                <div className="bg-bg-dark rounded-lg shadow-light border border-border py-40 text-center">
                    <div className="w-40 h-40 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-12">
                        <IconMessageCircle className="text-primary" size={18} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary mb-4">No responses yet</p>
                    <p className="text-[11px] text-text-secondary">Responses will appear here once nominees submit their feedback.</p>
                </div>
            )}
        </div>
    );
}
